import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import {
  Users, Send, Heart, MessageSquare, User, Award, ChevronDown, ChevronUp,
  Briefcase, Search
} from 'lucide-react';
import AppShell from '../components/AppShell';
import { StaggerContainer, FadeUp } from '../components/Animations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Community() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [members, setMembers] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('feed');
  const [expandedComments, setExpandedComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  const user = JSON.parse(localStorage.getItem('pivot_user') || '{}');

  useEffect(() => { loadPosts(); loadMembers(); }, []);

  const loadPosts = async () => {
    try { const r = await axios.get(`${API}/community/posts`); setPosts(r.data); } catch(e) {}
    setLoading(false);
  };
  const loadMembers = async () => {
    try { const r = await axios.get(`${API}/members/directory`); setMembers(r.data); } catch(e) {}
  };
  const handlePost = async (e) => {
    e.preventDefault();
    if (!newPost.trim()) return;
    try {
      await axios.post(`${API}/community/posts`, { user_id: user.user_id, content: newPost });
      setNewPost(''); toast.success('Post shared!'); loadPosts();
    } catch(e) { toast.error('Failed to share post'); }
  };
  const handleLike = async (postId) => {
    try {
      const r = await axios.post(`${API}/community/posts/${postId}/like`, { user_id: user.user_id });
      setPosts(prev => prev.map(p => p.post_id === postId ? {...p, likes: r.data.likes, liked_by: r.data.liked_by} : p));
    } catch(e) { toast.error('Failed'); }
  };
  const toggleComments = (postId) => setExpandedComments(prev => ({...prev, [postId]: !prev[postId]}));
  const handleComment = async (postId) => {
    const content = commentInputs[postId]?.trim();
    if (!content) return;
    try {
      const r = await axios.post(`${API}/community/posts/${postId}/comments`, { user_id: user.user_id, content });
      setPosts(prev => prev.map(p => p.post_id === postId ? {...p, comments: [...(p.comments || []), r.data]} : p));
      setCommentInputs(prev => ({...prev, [postId]: ''}));
    } catch(e) { toast.error('Failed'); }
  };

  const filteredMembers = members.filter(m =>
    m.user_id !== user.user_id &&
    (!searchQuery || m.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) || m.headline?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-6">
        {/* Header */}
        <FadeUp>
          <div className="mb-6">
            <h1 className="text-3xl font-cormorant font-bold text-white mb-1" data-testid="community-title">Power Community</h1>
            <p className="text-[#A39BB0] font-inter text-sm">Connect, share wins, and grow together</p>
          </div>
        </FadeUp>

        {/* Tabs */}
        <div className="flex space-x-1 mb-6 bg-white/[0.04] rounded-xl p-1 border border-white/[0.06]">
          {[
            { key: 'feed', label: 'Feed', icon: MessageSquare },
            { key: 'members', label: 'Members', icon: Users },
          ].map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                className={`flex items-center justify-center space-x-2 flex-1 px-5 py-2.5 rounded-lg text-sm font-inter font-medium transition-all ${
                  tab === t.key ? 'bg-white/10 text-white shadow-sm' : 'text-[#A39BB0] hover:text-white hover:bg-white/5'
                }`} data-testid={`tab-${t.key}`}>
                <Icon className="w-4 h-4" strokeWidth={1.5} />
                <span>{t.label}</span>
              </button>
            );
          })}
        </div>

        {tab === 'feed' ? (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Main Feed */}
            <div className="lg:col-span-2 space-y-4">
              {/* Post Input */}
              <FadeUp delay={0.1}>
                <form onSubmit={handlePost}>
                  <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-5">
                    <div className="flex items-start space-x-3">
                      <div className="w-11 h-11 rounded-full overflow-hidden flex-shrink-0">
                        {user.photo_url ? (
                          <img src={user.photo_url} alt="" className="w-11 h-11 rounded-full object-cover" />
                        ) : (
                          <div className="w-11 h-11 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                            <span className="text-white font-bold text-sm">{user.full_name?.charAt(0)}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1">
                        <textarea value={newPost} onChange={e => setNewPost(e.target.value)}
                          className="w-full bg-white/[0.05] border border-white/[0.08] rounded-xl px-4 py-3 text-white placeholder:text-[#A39BB0]/50 outline-none min-h-[80px] resize-none font-inter text-sm focus:border-purple-500/50 transition-colors"
                          placeholder="Share your insights, wins, or questions..." data-testid="community-post-input" />
                        <div className="flex justify-end mt-3">
                          <motion.button type="submit" disabled={!newPost.trim()}
                            whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                            className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-inter font-bold px-5 py-2 rounded-full text-sm disabled:opacity-40"
                            data-testid="community-post-submit">
                            <Send className="w-4 h-4" /><span>Post</span>
                          </motion.button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </FadeUp>

              {/* Posts */}
              {loading ? (
                <div className="text-center text-[#A39BB0] font-inter py-12">Loading...</div>
              ) : posts.length === 0 ? (
                <div className="text-center py-16">
                  <Users className="w-12 h-12 text-white/20 mx-auto mb-4" />
                  <h3 className="text-xl font-cormorant font-bold text-white mb-2">Start the conversation</h3>
                  <p className="text-[#A39BB0] font-inter text-sm">Share your journey or celebrate a win</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {posts.map(post => {
                    const isLiked = (post.liked_by || []).includes(user.user_id);
                    const commentsOpen = expandedComments[post.post_id];
                    const comments = post.comments || [];
                    return (
                      <FadeUp key={post.post_id}>
                        <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] hover:border-white/12 transition-all" data-testid="community-post">
                          <div className="p-5">
                            {/* Author Row */}
                            <div className="flex items-start space-x-3 mb-4">
                              <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
                                onClick={() => navigate(`/profile/${post.user_id}`)}>
                                {post.author_photo ? (
                                  <img src={post.author_photo} alt={post.author_name} className="w-12 h-12 rounded-full object-cover" />
                                ) : (
                                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                                    <User className="w-6 h-6 text-white" />
                                  </div>
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <button onClick={() => navigate(`/profile/${post.user_id}`)}
                                  className="text-white font-inter font-semibold text-sm hover:text-purple-300 transition-colors block">{post.author_name}</button>
                                <p className="text-[#A39BB0]/60 text-xs font-inter mt-0.5">
                                  {new Date(post.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                                </p>
                              </div>
                            </div>
                            {/* Content */}
                            <p className="text-white/85 font-inter text-sm leading-relaxed whitespace-pre-wrap">{post.content}</p>
                            {/* Engagement Bar */}
                            <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/[0.06]">
                              <div className="flex items-center space-x-5">
                                <button onClick={() => handleLike(post.post_id)}
                                  className={`flex items-center space-x-1.5 transition-all font-inter text-xs ${isLiked ? 'text-pink-400' : 'text-[#A39BB0] hover:text-pink-400'}`}
                                  data-testid={`like-button-${post.post_id}`}>
                                  <Heart className={`w-4 h-4 ${isLiked ? 'fill-pink-400' : ''}`} /><span>{post.likes || 0} Likes</span>
                                </button>
                                <button onClick={() => toggleComments(post.post_id)}
                                  className="flex items-center space-x-1.5 text-[#A39BB0] hover:text-purple-300 transition-colors font-inter text-xs"
                                  data-testid={`comment-toggle-${post.post_id}`}>
                                  <MessageSquare className="w-4 h-4" />
                                  <span>{comments.length} {comments.length === 1 ? 'Comment' : 'Comments'}</span>
                                </button>
                              </div>
                              <button onClick={() => toggleComments(post.post_id)} className="text-[#A39BB0]/50">
                                {commentsOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </button>
                            </div>
                          </div>
                          {/* Comments */}
                          {commentsOpen && (
                            <div className="border-t border-white/[0.06] bg-white/[0.02] rounded-b-2xl">
                              {comments.length > 0 && (
                                <div className="px-5 pt-4 space-y-3">
                                  {comments.map(c => (
                                    <div key={c.comment_id} className="flex items-start space-x-2.5" data-testid={`comment-${c.comment_id}`}>
                                      <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                                        {c.author_photo ? (
                                          <img src={c.author_photo} alt="" className="w-8 h-8 rounded-full object-cover" />
                                        ) : (
                                          <div className="w-8 h-8 bg-gradient-to-br from-purple-400 to-pink-400 rounded-full flex items-center justify-center">
                                            <span className="text-white text-[10px] font-bold">{c.author_name?.charAt(0)}</span>
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex-1 bg-white/[0.05] rounded-xl px-3 py-2 border border-white/[0.06]">
                                        <div className="flex items-center space-x-2">
                                          <span className="text-white font-inter font-medium text-xs">{c.author_name}</span>
                                          <span className="text-[#A39BB0]/40 text-[10px] font-inter">{new Date(c.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                        </div>
                                        <p className="text-white/60 font-inter text-xs mt-0.5 leading-relaxed">{c.content}</p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <div className="px-5 py-3 flex items-center space-x-2">
                                <input value={commentInputs[post.post_id] || ''}
                                  onChange={e => setCommentInputs(prev => ({...prev, [post.post_id]: e.target.value}))}
                                  onKeyDown={e => e.key === 'Enter' && handleComment(post.post_id)}
                                  placeholder="Write a comment..."
                                  className="flex-1 bg-white/[0.05] border border-white/[0.08] rounded-full px-4 py-2 text-sm font-inter text-white placeholder:text-[#A39BB0]/40 outline-none focus:border-purple-500/50"
                                  data-testid={`comment-input-${post.post_id}`} />
                                <button onClick={() => handleComment(post.post_id)}
                                  disabled={!commentInputs[post.post_id]?.trim()}
                                  className="w-8 h-8 bg-gradient-to-r from-purple-600 to-pink-500 rounded-full flex items-center justify-center text-white disabled:opacity-40"
                                  data-testid={`comment-submit-${post.post_id}`}>
                                  <Send className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </FadeUp>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Right Sidebar - People You May Know */}
            <div className="space-y-4">
              <div className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] p-5">
                <h3 className="text-sm font-inter font-semibold text-white mb-4">People You May Know</h3>
                <div className="space-y-3">
                  {members.filter(m => m.user_id !== user.user_id).slice(0, 5).map((member, i) => (
                    <div key={member.user_id || i}
                      className="flex items-center space-x-3 cursor-pointer hover:bg-white/[0.03] rounded-lg p-2 -mx-2 transition-colors"
                      onClick={() => navigate(`/profile/${member.user_id}`)} data-testid={`sidebar-member-${i}`}>
                      <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0">
                        {member.photo_url ? (
                          <img src={member.photo_url} alt={member.full_name} className="w-10 h-10 rounded-full object-cover" />
                        ) : (
                          <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs font-bold">{member.full_name?.charAt(0)}</span>
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-white font-inter text-xs font-medium truncate">{member.full_name}</h4>
                        <p className="text-[#A39BB0] text-[10px] font-inter truncate">{member.headline || member.archetype || 'Member'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Members Tab */
          <div>
            <div className="mb-6">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A39BB0]" />
                <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search members by name or role..."
                  className="w-full bg-white/[0.05] border border-white/[0.08] rounded-xl pl-11 pr-4 py-3 text-sm font-inter text-white placeholder:text-[#A39BB0]/40 outline-none focus:border-purple-500/50"
                  data-testid="member-search" />
              </div>
            </div>

            <StaggerContainer className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredMembers.map((member, i) => (
                <FadeUp key={member.user_id || i}>
                  <motion.div whileHover={{ y: -3 }}
                    className="bg-white/[0.07] backdrop-blur-xl rounded-2xl border border-white/[0.08] hover:border-white/12 transition-all overflow-hidden"
                    data-testid={`member-card-${i}`}>
                    {/* Banner */}
                    <div className="h-16 bg-gradient-to-r from-purple-600/30 to-pink-500/30" />
                    <div className="px-5 pb-5 -mt-6">
                      <div className="flex items-end space-x-3 mb-3">
                        <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-[#1A0533] flex-shrink-0 cursor-pointer"
                          onClick={() => navigate(`/profile/${member.user_id}`)}>
                          {member.photo_url ? (
                            <img src={member.photo_url} alt={member.full_name} className="w-14 h-14 rounded-full object-cover" />
                          ) : (
                            <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                              <span className="text-white font-cormorant font-bold text-xl">{member.full_name?.charAt(0)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <button onClick={() => navigate(`/profile/${member.user_id}`)}
                        className="text-white font-inter font-semibold text-sm hover:text-purple-300 transition-colors block mb-0.5">{member.full_name}</button>
                      {member.headline && <p className="text-[#A39BB0] text-xs font-inter line-clamp-2 mb-2">{member.headline}</p>}
                      <div className="flex items-center flex-wrap gap-2">
                        {member.archetype && (
                          <span className="inline-flex items-center space-x-1 bg-purple-500/15 text-purple-300 px-2 py-0.5 rounded-full text-[10px] font-inter font-medium border border-purple-500/20">
                            <Award className="w-3 h-3" /><span>{member.archetype}</span>
                          </span>
                        )}
                        {member.industry && (
                          <span className="inline-flex items-center space-x-1 bg-white/5 text-[#A39BB0] px-2 py-0.5 rounded-full text-[10px] font-inter border border-white/10">
                            <Briefcase className="w-3 h-3" /><span>{member.industry}</span>
                          </span>
                        )}
                      </div>
                      <button onClick={() => navigate(`/profile/${member.user_id}`)}
                        className="w-full mt-3 text-center text-purple-300 hover:text-white text-xs font-inter font-medium py-2 bg-white/[0.05] rounded-lg border border-white/[0.08] hover:bg-white/10 transition-all"
                        data-testid={`view-profile-${i}`}>
                        View Profile
                      </button>
                    </div>
                  </motion.div>
                </FadeUp>
              ))}
            </StaggerContainer>
          </div>
        )}
      </div>
    </AppShell>
  );
}
